import * as React from "react";
import { classNames } from "../../utils";
import "./dropdownSelect.scss";
import ArrowDown from "./ArrowDown";
import SearchBox from "./SearchBox";
import { FixedSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

const { useState, useRef } = React;

interface IDropdownItem {
  label: string;
  value: any;
}

const emptyItem = { label: "", value: "" };

export type TypeItemRenderer = (obj: IDropdownItem) => JSX.Element;

export interface IDropdownSelectProps {
  className?: string;
  style?: React.CSSProperties;
  inputLabel: string;
  inputId: string;
  errorMessage: string;
  initValue?: string;
  forceUpdate?: { value: string };
  dropdownItemList: IDropdownItem[];
  selectedItemRenderer?: TypeItemRenderer;
  listItemRenderer?: TypeItemRenderer;
  onSelect: (item: IDropdownItem & { inputId: string }) => void;
  cluster?: { [key: string]: () => boolean };
  enableSearch?: boolean;
  optional?: boolean;
  disabled?: boolean;
}

const DropdownSelect: React.FC<IDropdownSelectProps> = (props) => {
  const {
    className,
    style,
    inputLabel,
    inputId,
    errorMessage,
    initValue,
    forceUpdate,
    dropdownItemList,
    selectedItemRenderer,
    listItemRenderer,
    onSelect,
    cluster,
    enableSearch,
    optional,
  } = props;

  const [selectedItem, setSelectedItem] = useState<IDropdownItem>(emptyItem);
  const wrapperElem = useRef<HTMLDivElement>(null);
  const searchInputElem = useRef<HTMLInputElement>(null);
  const [isValid, setIsValid] = useState<boolean>(null);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [searchContent, setSearchContent] = useState<string>("");
  const [listMaxHeight, setListMaxHeight] = useState<number>(0);

  React.useEffect(() => {
    if (initValue !== undefined && initValue !== null) {
      setIsValid(true);
      setSelectedItem(
        dropdownItemList.find((item) => item.value === initValue)
      );
    }
    // eslint-disable-next-line
  }, [dropdownItemList]);

  React.useEffect(() => {
    if (forceUpdate && forceUpdate.value) {
      setSelectedItem(
        dropdownItemList.find((item) => item.value === forceUpdate.value)
      );
      setIsValid(true);
    }
    // eslint-disable-next-line
  }, [forceUpdate]);

  React.useEffect(() => {
    if (cluster) {
      cluster[inputId] = validationHandler;
    }
    return () => {
      if (cluster && cluster[inputId]) {
        delete cluster[inputId];
      }
    };
  });

  React.useEffect(() => {
    if (isFocused && wrapperElem.current) {
      setListMaxHeight(
        window.innerHeight -
          wrapperElem.current.getBoundingClientRect().bottom -
          (enableSearch ? 70 : 10)
      );
    }
  }, [isFocused, wrapperElem, enableSearch, dropdownItemList]);

  React.useEffect(() => {
    const body = document.getElementsByTagName("body")[0];
    if (isFocused && dropdownItemList.length > 3) {
      body.setAttribute("data-lock", "true");
    } else {
      body.removeAttribute("data-lock");
    }
    // eslint-disable-next-line
  }, [isFocused]);

  const validationHandler = () => {
    if (
      !optional &&
      (selectedItem.value === "" || selectedItem.value === undefined)
    ) {
      setIsValid(false);
      return false;
    } else {
      return true;
    }
  };

  const onSelectHandler = (item: IDropdownItem) => {
    if (onSelect) {
      onSelect({ ...item, inputId });
    }
    setSelectedItem(item);
  };

  const isInsideSwap = (event: React.FocusEvent) => {
    const detectElem = event.relatedTarget || document.activeElement;
    const isInsideSwap =
      (detectElem === wrapperElem.current && isFocused) ||
      (detectElem === searchInputElem.current && isFocused) ||
      isCovering(
        wrapperElem.current.getBoundingClientRect(),
        document.activeElement.getBoundingClientRect()
      );
    return isInsideSwap;
  };

  const onBlurHandler = (event: React.FocusEvent) => {
    if (isInsideSwap(event)) return;
    setIsFocused(false);
  };

  const onFocusHandler = (event: React.FocusEvent) => {
    if (props.disabled) return;
  
    setTimeout(() => {
      setIsFocused(true);
  
      if (searchInputElem.current) {
        setTimeout(() => searchInputElem.current?.focus(), 50);
      }
    }, 150);
  };
  
  const onClickFocusHandler = (event: React.MouseEvent) => {
    if (props.disabled) return;
    if (!isFocused && searchInputElem.current) {
      setTimeout(() => searchInputElem.current.focus(), 50);
    }
    setIsFocused(!isFocused);
  };
  const itemClickHandler = (event: React.MouseEvent, item: IDropdownItem) => {
    event.preventDefault();
    event.stopPropagation();
    setIsFocused(false);
    setIsValid(true);
    onSelectHandler(item);
  };

  // const renderDropdownList = (list: IDropdownItem[]) => {
  //   return list.map()
  // }

  const showErrorMessage = isValid === false;

  const filteredList = !searchContent
    ? dropdownItemList
    : dropdownItemList.filter((item) =>
        ` ${item.value} ${item.label}`
          .toLowerCase()
          .includes(" " + searchContent.toLowerCase())
      );

  const listMinHeight = (size: number) => {
    const minSize = size <= 1 ? 1 : size >= 3 ? 3 : size;
    return `calc(${minSize} * 60px)`;
  };

  return (
    <div
      tabIndex={0}
      style={style}
      className={classNames({
        [className]: !!className,
        inputField: true,
        dropdownSelect: true,
        "dropdownSelect--focus": isFocused,
        "inputField--warning": isValid === false,
        "inputField--active": isValid,
      })}
      onClick={onClickFocusHandler}
      onBlur={onBlurHandler}
      ref={wrapperElem}
      onFocus={onFocusHandler}
    >
      {/* LABEL */}
      <label
        className={classNames({
          inputField__label: true,
          "inputField__label--onTop": isFocused || isValid === true,
        })}
      >
        {inputLabel + (showErrorMessage ? ` ${errorMessage}` : "")}
      </label>

      {/* VALUE DISPLAY */}
      <div
        className="textInput__content dropdownSelect__valueText"
        data-name={inputId}
      >
        {selectedItemRenderer && !!selectedItem.value
          ? selectedItemRenderer(selectedItem)
          : selectedItem?.label}
      </div>

      <div className="dropdownSelect__icon">
        <ArrowDown color="rgba(0, 0, 0, 0.25)" />
      </div>
      {/* DROPDOWN LIST */}
      <div
        className={classNames({
          dropdownSelect__floatArea: true,
          "dropdownSelect__floatArea--long": dropdownItemList.length > 3,
          "dropdownSelect__floatArea--hidden": !isFocused,
        })}
      >
        {enableSearch && (
          <SearchBox
            value={searchContent}
            refElem={searchInputElem}
            onChange={(event) => setSearchContent(event.target.value)}
            closeFn={() => setIsFocused(false)}
          />
        )}
        <div
          className={classNames({
            dropdownSelect__list: true,
            "dropdownSelect__list--long": dropdownItemList.length > 3,
          })}
          style={{
            ["--dropdown-max-height" as any]: listMaxHeight + "px",
            ["--dropdown-min-height" as any]: listMinHeight(
              filteredList.length
            ),
          }}
        >
          {isFocused && (
            <AutoSizer>
              {({ height, width }: any) => (
                
                <FixedSizeList
                  height={height}
                  itemSize={60}
                  itemCount={filteredList.length}
                  width={width}
                >
                  {(props) => {
                    const { index, style } = props;
                    const item = filteredList[index];
                    return (
                      <div
                        style={style}
                        className="dropdownSelect__item"
                        onClick={(event) => itemClickHandler(event, item)}
                      >
                        {listItemRenderer ? listItemRenderer(item) : item.label}
                      </div>
                    );
                  }}
                </FixedSizeList>
              )}
            </AutoSizer>
          )}
        </div>
      </div>
    </div>
  );
};

export default DropdownSelect;

function isCovering(
  wrapper: { top: number; bottom: number; left: number; right: number },
  inner: { top: number; bottom: number; left: number; right: number }
) {
  return (
    wrapper.top < inner.top &&
    wrapper.bottom > inner.bottom &&
    wrapper.left < inner.left &&
    wrapper.right > inner.right
  );
}
